import { Box, Button, Dialog, DialogActions, DialogContent, MenuItem, TextField, Typography } from "@mui/material";
import DialogTitle from "apps/web/src/componentLibrary/DialogTitle";
import { useSnackbar } from "apps/web/src/components/Snackbar/SnackbarContext";
import { useState } from "react";
import { Role, useBulkAssignRoleToOrgMembershipsMutation } from "../../../types";
import { AdminOrgMembershipTableRow } from "./Table";

// Same roles assignable in the Edit Member modal (NUT-2074), minus kiosk.
const BULK_ASSIGNABLE_ROLES: Role[] = ["athlete", "dietitian", "chef", "foodmanager", "nonRdAdmin"];

const ROLE_LABELS: Record<string, string> = {
  athlete: "Athlete",
  dietitian: "Dietitian",
  chef: "Chef",
  foodmanager: "Food Manager",
  nonRdAdmin: "Non-RD Admin",
};

interface BulkAssignRoleModalProps {
  open: boolean;
  onClose: () => void;
  orgId: string;
  selectedMembers: AdminOrgMembershipTableRow[];
  onAssigned: () => void;
}

export const BulkAssignRoleModal = ({ open, onClose, orgId, selectedMembers, onAssigned }: BulkAssignRoleModalProps) => {
  const { setMessage } = useSnackbar();
  const [role, setRole] = useState<Role | "">("");
  const [bulkAssignRole, { loading }] = useBulkAssignRoleToOrgMembershipsMutation();

  // Athlete and dietitian are mutually exclusive, so an option is disabled when the selection already
  // contains the conflicting role (prevent-invalid). Computed across the whole selection.
  const anyDietitian = selectedMembers.some(m => m.roles.includes("dietitian"));
  const anyAthlete = selectedMembers.some(m => m.roles.includes("athlete"));
  const isRoleDisabled = (r: Role) => (r === "athlete" && anyDietitian) || (r === "dietitian" && anyAthlete);

  const handleAssign = async () => {
    if (!role) {
      return;
    }
    try {
      const res = await bulkAssignRole({
        variables: { input: { orgId, orgMembershipIds: selectedMembers.map(m => m.id), role } },
      });
      const count = res.data?.bulkAssignRoleToOrgMemberships.updatedCount ?? selectedMembers.length;
      setMessage("success", `${count} members have been updated`);
      onAssigned();
      onClose();
    } catch (e: any) {
      setMessage("error", e.message);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle title="Assign Roles" onClose={onClose} />
      <DialogContent>
        <Box sx={{ bgcolor: "grey.100", borderRadius: 1, p: 1.5, mb: 2 }}>
          <Typography variant="body2">Selected members: {selectedMembers.length}</Typography>
        </Box>
        <TextField select fullWidth label="Roles" margin="dense" value={role} onChange={e => setRole(e.target.value as Role)}>
          {BULK_ASSIGNABLE_ROLES.map(r => (
            <MenuItem key={r} value={r} disabled={isRoleDisabled(r)}>
              {ROLE_LABELS[r]}
            </MenuItem>
          ))}
        </TextField>
      </DialogContent>
      <DialogActions>
        <Button variant="outlined" onClick={onClose}>
          Cancel
        </Button>
        <Button variant="contained" onClick={handleAssign} disabled={!role || loading}>
          Assign Roles
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default BulkAssignRoleModal;
